/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box, Button, CircularProgress, FormHelperText, Link, TextField } from '@mui/material';
import { ApiError, getErrorMessage } from '@pro4-erp/core-web';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { changePassword } from '../api/changePassword';
import { validateRecoverHash } from '../api/validateRecoverHash';
import { ResetPasswordData, ResetPasswordForm, ResetPasswordPageProps } from '../types';

const Page = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { recoverHash } = useParams<ResetPasswordPageProps>() as ResetPasswordPageProps;
  const navigate = useNavigate();

  const form = useForm<ResetPasswordForm>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      // redirect user back to login screen
      navigate('/auth/login');

      // show toast
      enqueueSnackbar(
        t('Your password was successfully changed. You can login now.', {
          defaultValue: 'Your password was successfully changed. You can login now.',
        }),
        { variant: 'success', autoHideDuration: 10000 }
      );
    },
    onError: (error: ApiError) => {
      form.setError('root', { message: getErrorMessage(error.errorResponseDto.message) });
    },
  });

  const query = useQuery<ResetPasswordData, ApiError>(
    ['validate-recover-hash', recoverHash],
    () => validateRecoverHash({ recoverHash }),
    { retry: false, refetchOnWindowFocus: false }
  );

  const onSubmit = (data: ResetPasswordForm) => {
    form.clearErrors();
    const { password, confirmPassword } = data;
    changePasswordMutation.mutate({
      password,
      confirmPassword,
      recoverHash,
    });
  };

  if (query.isLoading) {
    return (
      <Box pt={2}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Box pt={2} sx={{ textAlign: 'center' }}>
        <FormHelperText error sx={{ textAlign: 'center' }}>
          {query.error.message}
        </FormHelperText>
        <Link
          variant="body2"
          sx={{ cursor: 'pointer', textAlign: 'center' }}
          onClick={() => navigate('/auth/login')}
        >
          {t('Back to login page', {
            defaultValue: 'Back to login page',
          })}
        </Link>
      </Box>
    );
  }

  return (
    /* form container */
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mt: '1rem',
      }}
    >
      {/* form helper text */}
      <FormHelperText>
        {t('Please enter your new password, the password should be at least 6 characters long.', {
          defaultValue:
            'Please enter your new password, the password should be at least 6 characters long.',
        })}
      </FormHelperText>
      {/* form */}
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        sx={{ width: '100%' }}
      >
        {/* password field */}
        <Controller
          control={form.control}
          name="password"
          rules={{
            required: t('Password is required.', {
              defaultValue: 'Password is required.',
            }).toString(),
            minLength: {
              value: 6,
              message: t('Must be at least 6 characters long.', {
                defaultValue: 'Must be at least 6 characters long.',
              }),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mt: '1rem' }}
              label={t('Password', { defaultValue: 'Password' })}
              type="password"
              fullWidth
              error={!!form.formState.errors.password}
              helperText={form.formState.errors.password?.message?.toString()}
            />
          )}
        />
        {/* confirm password field */}
        <Controller
          control={form.control}
          name="confirmPassword"
          rules={{
            required: 'Confirm password is required.',
            validate: (value) => {
              if (form.watch('password') !== value) {
                return t('Passwords do not match.', {
                  defaultValue: 'Passwords do not match.',
                }).toString();
              }
              return undefined;
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              sx={{ mt: '1rem' }}
              label={t('Confirm password', { defaultValue: 'Confirm password' })}
              type="password"
              fullWidth
              error={!!form.formState.errors.confirmPassword}
              helperText={form.formState.errors.confirmPassword?.message?.toString()}
            />
          )}
        />

        {/* form validation error output */}
        {!!form.formState.errors.root && (
          <FormHelperText error>{form.formState.errors.root.message?.toString()}</FormHelperText>
        )}
        {/* submit button */}
        <Button
          type="submit"
          variant="contained"
          sx={{ mt: '1rem' }}
          disabled={changePasswordMutation.isLoading}
          fullWidth
        >
          {t('Submit', { defaultValue: 'Submit' })}
        </Button>
      </Box>
    </Box>
  );
};

export default Page;
